import React, { useEffect, useState, useRef } from 'react';
import offer1 from '../assets/i1.png';
import offer2 from '../assets/i2.png';
import offer3 from '../assets/i3.png';
import { Link } from 'react-router-dom'; // For standard page navigation
import { HashLink } from 'react-router-hash-link'; // For hash links

const OffersSection = () => {
  const images = [offer1, offer2, offer3];
  const [currentIndex, setCurrentIndex] = useState(0);

  // New state for scroll-triggered text animations
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null); // Ref for the section to observe

  // Effect for Image Carousel (only changes image source, no entry animation)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [images.length]);

  // Effect for Intersection Observer (for scroll-triggered text animations)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!inView) {
              setInView(true);
            }
          } else {
            if (inView) {
              setInView(false); // Reset animation state when out of view
            }
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the component is visible
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [inView]);

  const containerStyle = {
    display: 'flex',
    flexWrap: 'wrap', // Allows wrapping on smaller screens
    padding: '80px 5%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '40px',
    backgroundColor: '#fff',
  };

  const imageContainerStyle = {
    flex: '1 1 400px', // Flexible sizing for responsiveness
    maxWidth: '500px', // Prevent image from getting too wide on large screens
    textAlign: 'center',
    height: '450px', // Maintain a fixed height for layout stability
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    position: 'relative', // Needed for the ::after pseudo-element
    borderRadius: '8px', // Optional: slight rounded corners for image container
    // box-shadow is now handled in CSS for removal
  };

  const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'contain', // Ensures the entire image is visible within its bounds
    display: 'block', // Ensures img takes full space and no extra margin
    // Removed transform transition as we are removing transform on hover
  };

  // Helper for scroll-triggered text animations (slide up, fade in)
  const getScrollAnimatedTextStyle = (delay) => ({
    transform: inView ? 'translateY(0)' : 'translateY(50px)',
    opacity: inView ? 1 : 0,
    transition: inView
      ? `transform 0.8s ease-out ${delay}s, opacity 0.8s ease-out ${delay}s`
      : 'none', // Reset instantly when out of view
  });

  const buttonContainerStyle = {
    display: 'flex',
    gap: '20px',
    marginTop: '30px',
    flexWrap: 'wrap', // Allow buttons to wrap on smaller screens
  };

  // Base style for buttons (will use CSS classes for specific hover effects)
  const baseButtonStyle = {
    padding: '12px 15px',
    borderRadius: '25px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    textDecoration: 'none',
    position: 'relative', // Needed for pseudo-elements for hover effect
    overflow: 'hidden',   // Needed to contain the pseudo-element sweep
    zIndex: 1,
  };

  return (
    <>
      <style>
        {`
          /* Image Hover Mirror Effect (Diagonal Sweep) */
          .offers-image-card {
            position: relative;
            overflow: hidden;
            border-radius: 8px; /* Match container */
            cursor: pointer; /* Indicate interactivity */
            // REMOVED: box-shadow: '0 4px 8px rgba(0,0,0,0.1)';
          }

          // REMOVED: .offers-image-card img { transition: transform 0.3s ease-in-out; }
          // REMOVED: .offers-image-card:hover img { transform: scale(1.03); }

          .offers-image-card::after {
            content: '';
            position: absolute;
            width: 150%; /* Wider to cover diagonal sweep */
            height: 100%;
            background: rgba(255, 255, 255, 0.2); /* Semi-transparent white */
            transform: translateX(-100%) rotate(45deg); /* Start off-screen to the left, rotated */
            transition: transform 0.5s ease-out; /* Smooth transition for the effect */
            z-index: 1; /* Ensure it's above the image */
            top: 0;
            left: 0;
            pointer-events: none; /* Allows clicks to pass through */
          }

          .offers-image-card:hover::after {
            transform: translateX(100%) rotate(45deg); /* Sweep across to the right on hover */
          }

          /* Text Scroll-in Animation */
          @keyframes textFadeSlideIn {
            0% { opacity: 0; transform: translateY(50px); }
            100% { opacity: 1; transform: translateY(0); }
          }

          /* Button Hover Animation (Fill from left) */
          .offer-button { /* Common class for both buttons */
            color: #fff; /* Default text color for primary button */
            background-color: #D4A762; /* Default background for primary button */
            border: none;
            transition: background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease; /* Smooth transitions */
          }

          .offer-button.secondary { /* Specific styles for the secondary button */
            background-color: #fff;
            color: #D4A762;
            border: 2px solid #D4A762;
          }

          .offer-button::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 0%;
            height: 100%;
            background-color: #b88e50; /* Darker gold for primary button fill */
            transition: width 0.3s ease-out;
            z-index: -1; /* Behind the text */
          }

          .offer-button.secondary::before {
            background-color: #D4A762; /* Gold for secondary button fill */
          }

          .offer-button:hover::before {
            width: 100%; /* Expand to full width on hover */
          }

          .offer-button:hover {
            color: #fff; /* Ensure text is white on hover for both */
            box-shadow: 0 4px 10px rgba(0,0,0,0.2); /* Subtle shadow on hover for buttons */
          }


          /* General styling for list item hover */
          .offers-list-item:hover {
            color: #D4A762;
            transform: translateX(5px);
            transition: all 0.3s ease;
          }
        `}
      </style>

      <section style={containerStyle} ref={sectionRef}>
        {/* Left: Image Carousel with Hover Effect */}
        <div style={imageContainerStyle} className="offers-image-card">
          <img
            key={currentIndex} // Keep key to ensure React swaps the img element
            src={images[currentIndex]}
            alt={`Offer ${currentIndex + 1}`}
            style={imageStyle}
          />
        </div>

        {/* Right: Text Content with Scroll-in Animation */}
        <div style={{ flex: '1 1 400px', maxWidth: '500px', ...getScrollAnimatedTextStyle(0) }}>
          <h4
            style={{
              ...getScrollAnimatedTextStyle(0.1), // Staggered delay for h4
              color: '#D4A762',
              fontSize: '18px',
              marginBottom: '10px',
              letterSpacing: '1px',
              fontWeight: '500'
            }}
          >
            OUR DAILY OFFERS
          </h4>
          <h2
            style={{
              ...getScrollAnimatedTextStyle(0.3), // Staggered delay for h2
              fontSize: 'clamp(30px, 5vw, 48px)',
              fontWeight: 'normal',
              lineHeight: '1.2',
              marginBottom: '20px',
              fontFamily: 'Playfair Display',
            }}
          >
            Taste The Savings With <br />
            our <span style={{ color: '#D4A762', fontFamily: 'Playfair Display' }}>Daily Special</span>
          </h2>
          <p
            style={{
              ...getScrollAnimatedTextStyle(0.5), // Staggered delay for p
              fontSize: '16px',
              color: '#444',
              marginBottom: '25px',
            }}
          >
            Every day is an opportunity to enjoy your favorites at a discounted
            price. Explore our daily rotating specials and indulge in flavorful
            meals at a fraction of the cost.
          </p>

          <ul
            style={{
              padding: 0,
              listStyle: 'none',
              color: '#333',
              fontSize: '16px',
              display: 'flex',
              flexDirection: 'column',
              flexWrap: 'wrap',
            }}
          >
            {[
              'Seasonal & Locally Sourced Ingredients',
              'Vegetarian & Dietary-Friendly Options',
              'Exquisite Pairings & Unique Flavors',
            ].map((item, idx) => (
              <li
                key={idx} // Using index is fine here as list items are static
                className="offers-list-item"
                style={{
                  ...getScrollAnimatedTextStyle(0.7 + idx * 0.1), // Staggered delay for list items
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  whiteSpace: 'normal',
                }}
              >
                <span
                  style={{
                    height: '8px',
                    width: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#D4A762',
                    display: 'inline-block',
                    marginRight: '12px',
                    flexShrink: 0,
                  }}
                ></span>
                {item}
              </li>
            ))}
          </ul>

          <div style={buttonContainerStyle}>
            {/* Book A Table Button */}
            <HashLink
              to="/#contact-us"
              smooth
              style={baseButtonStyle}
              className="offer-button" // Apply common button class
            >
              Book A Table <span style={{ fontSize: '14px' }}>→</span>
            </HashLink>

            {/* Explore Menu Button */}
            <Link
              to="/menu"
              style={baseButtonStyle}
              className="offer-button secondary" // Apply common and secondary specific classes
            >
              Explore Menu <span style={{ fontSize: '14px' }}>→</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default OffersSection;